import { useMemo, useRef, type MouseEvent } from 'react';
import { type TreeNode } from 'primereact/treenode';
import {
    type TreeSelectCheckboxInteraction,
    type TreeSelectValue,
} from '../models/tree-select-checkbox-selection-behavior';
import {
    buildNodeMaps,
    normalizeAncestorsOnlySelection,
    removeSelectedValue,
} from '../utils/tree-select-checkbox-selection.utils';
import {
    getCanonicalTreeSelectValue,
    getTreeSelectSelectionFlags,
} from '../utils/tree-select-selection-model.utils';
import { createChangeEvent } from '../utils/tree-select.utils';
import {
    type TreeSelectChangeEvent,
    type TreeSelectProps,
} from '../models/tree-select.types';
import { useTreeSelectValuePresentation } from './UseTreeSelectValuePresentation';

type TreeSelectNodeSelectEvent = Parameters<
    NonNullable<TreeSelectProps['onNodeSelect']>
>[0];

type TreeSelectNodeUnselectEvent = Parameters<
    NonNullable<TreeSelectProps['onNodeUnselect']>
>[0];

export interface TreeSelectSelectionModel {
    display: TreeSelectProps['display'];
    selectedNodes: TreeNode[];
    shouldRenderSelectedPills: boolean;
    value: TreeSelectValue;
    valueTemplate: TreeSelectProps['valueTemplate'];
    handleChange: (event: TreeSelectChangeEvent) => void;
    handleDismissSelectedValue: (
        key: string,
        event: MouseEvent<HTMLButtonElement>,
    ) => void;
    handleNodeSelect: (event: TreeSelectNodeSelectEvent) => void;
    handleNodeUnselect: (event: TreeSelectNodeUnselectEvent) => void;
}

export function useTreeSelectSelectionModel(
    props: TreeSelectProps,
): TreeSelectSelectionModel {
    const {
        checkboxSelectionBehavior = 'native',
        onChange,
        onNodeSelect,
        onNodeUnselect,
        options,
        selectionMode,
        selectedValueLayout,
        value,
        valueTemplate: inputValueTemplate,
    } = props;

    const interactionRef = useRef<TreeSelectCheckboxInteraction | null>(null);
    const selectionFlags = useMemo(
        () =>
            getTreeSelectSelectionFlags({
                checkboxSelectionBehavior,
                display: props.display,
                inputValueTemplate,
                options,
                selectedValueLayout,
                selectionMode,
            }),
        [
            checkboxSelectionBehavior,
            inputValueTemplate,
            options,
            props.display,
            selectedValueLayout,
            selectionMode,
        ],
    );

    const nodeMaps = useMemo(
        () =>
            selectionFlags.shouldBuildNodeMaps
                ? buildNodeMaps(options)
                : undefined,
        [options, selectionFlags.shouldBuildNodeMaps],
    );

    const normalizedValue = useMemo(() => {
        return getCanonicalTreeSelectValue(
            value,
            nodeMaps,
            selectionFlags.isAncestorsOnlyCheckboxMode,
        );
    }, [nodeMaps, selectionFlags.isAncestorsOnlyCheckboxMode, value]);

    function emitNormalizedChange(event: TreeSelectChangeEvent): void {
        if (!selectionFlags.isAncestorsOnlyCheckboxMode || !nodeMaps) {
            onChange?.(event);
            return;
        }

        const nextValue = normalizeAncestorsOnlySelection({
            nextValue: event.value,
            previousValue: normalizedValue,
            nodeMaps,
            interaction: interactionRef.current,
        });
        interactionRef.current = null;

        onChange?.({
            ...event,
            value: nextValue,
            target: {
                ...event.target,
                value: nextValue,
            },
        });
    }

    function handleNodeSelect(event: TreeSelectNodeSelectEvent): void {
        if (selectionFlags.isAncestorsOnlyCheckboxMode) {
            interactionRef.current = {
                action: 'select',
                key: String(event.node.key),
            };
        }

        onNodeSelect?.(event);
    }

    function handleNodeUnselect(event: TreeSelectNodeUnselectEvent): void {
        if (selectionFlags.isAncestorsOnlyCheckboxMode) {
            interactionRef.current = {
                action: 'unselect',
                key: String(event.node.key),
            };
        }

        onNodeUnselect?.(event);
    }

    function handleDismissSelectedValue(
        key: string,
        event: MouseEvent<HTMLButtonElement>,
    ): void {
        if (!nodeMaps || !selectionMode) {
            return;
        }

        const nextValue = removeSelectedValue({
            key,
            nodeMaps,
            selectionMode,
            value: normalizedValue,
            preserveAncestorsOnlySelection:
                selectionFlags.isAncestorsOnlyCheckboxMode,
        });

        emitNormalizedChange(createChangeEvent(event, props, nextValue));
    }

    const { display, selectedNodes, shouldRenderSelectedPills, valueTemplate } =
        useTreeSelectValuePresentation({
            display: props.display,
            inputValueTemplate,
            nodeMaps,
            placeholder: props.placeholder,
            selectionFlags,
            selectionMode,
            value: normalizedValue,
        });

    return {
        display,
        selectedNodes,
        shouldRenderSelectedPills,
        value: normalizedValue,
        valueTemplate,
        handleChange: emitNormalizedChange,
        handleDismissSelectedValue,
        handleNodeSelect,
        handleNodeUnselect,
    };
}
